import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { startOfWeek, endOfWeek, subDays, format } from "date-fns";

export interface ProfileData {
  current_streak: number | null;
  last_activity_date: string | null;
}

export interface FlashcardDeckStats {
  id: string;
  title: string;
  total_cards: number;
  mastered_count: number;
  learning_count: number;
  not_studied_count: number;
  last_studied: string | null;
}

export interface FlashcardReviewStats {
  rating: number;
  count: number;
}

export interface StudySessionData {
  date: string;
  studied_count: number;
  due_count: number;
}

export interface QuizAttemptStats {
  id: string;
  quiz_id: string;
  score: number | null;
  total_questions: number;
  total_correct: number | null;
  completed_at: string | null;
  difficulty: string;
  question_types: string[];
}

export interface QuizDifficultyStats {
  difficulty: string;
  avg_score: number;
  attempt_count: number;
}

export interface QuizQuestionTypeStats {
  question_type: string;
  correct: number;
  total: number;
  accuracy: number;
}

export interface WeakTopic {
  topic: string;
  correct: number;
  total: number;
  accuracy: number;
}

export interface ExamAttemptStats {
  id: string;
  exam_id: string;
  score: number | null;
  total_questions: number;
  total_correct: number | null;
  submitted_at: string | null;
  title: string | null;
}

export interface ExamQuestionTypeStats {
  question_type: string;
  correct: number;
  total: number;
  accuracy: number;
}

export interface ChatStats {
  totalChats: number;
  totalMessages: number;
  avgMessagesPerChat: number;
  activityByDay: { date: string; messages: number }[];
  topTopics: { title: string; messageCount: number }[];
}

export interface StudyTimeStats {
  totalMinutes: number;
  flashcardsMinutes: number;
  quizzesMinutes: number;
  examsMinutes: number;
  weeklyMinutes: number;
  dailyAverage: number;
  breakdown: { activity: string; minutes: number; percentage: number }[];
}

export interface ReportsData {
  profile: ProfileData | null;
  flashcardDecks: FlashcardDeckStats[];
  flashcardReviews: FlashcardReviewStats[];
  studySessions: StudySessionData[];
  weeklyStudySessions: number;
  totalCardsStudied: number;
  totalCardsDue: number;
  quizAttempts: QuizAttemptStats[];
  quizDifficultyStats: QuizDifficultyStats[];
  quizQuestionTypeStats: QuizQuestionTypeStats[];
  quizWeakTopics: WeakTopic[];
  examAttempts: ExamAttemptStats[];
  examQuestionTypeStats: ExamQuestionTypeStats[];
  overallQuizAccuracy: number;
  overallExamAccuracy: number;
  chatStats: ChatStats;
  studyTime: StudyTimeStats;
}

export const useReportsData = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["reports-data", user?.id],
    queryFn: async (): Promise<ReportsData> => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
      const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 });
      const last30Days = subDays(new Date(), 30);

      // Fetch all data in parallel
      const [
        profileResult,
        flashcardDecksResult,
        flashcardsResult,
        flashcardReviewsResult,
        studySessionsResult,
        quizAttemptsResult,
        quizAnswersResult,
        quizzesResult,
        examAttemptsResult,
        examAnswersResult,
        examQuestionsResult,
        chatContentsResult,
        chatMessagesResult,
      ] = await Promise.all([
        // Profile data
        supabase
          .from("profiles")
          .select("current_streak, last_activity_date")
          .eq("id", user.id)
          .single(),
        
        // Flashcard decks (exclude archived)
        supabase
          .from("flashcard_decks")
          .select("id, title, total_cards, updated_at, content_id, is_archived")
          .eq("user_id", user.id)
          .eq("is_archived", false),
        
        // Flashcards for mastery calculation (include created_at and updated_at)
        supabase
          .from("flashcards")
          .select("id, deck_id, ef, interval, repetitions, due_at, created_at, updated_at")
          .eq("user_id", user.id),
        
        // Flashcard reviews for rating distribution
        supabase
          .from("flashcard_reviews")
          .select("rating, reviewed_at")
          .eq("user_id", user.id),
        
        // Study sessions for the last 30 days
        supabase
          .from("flashcard_study_sessions")
          .select("created_at, studied_count, due_count")
          .eq("user_id", user.id)
          .gte("created_at", last30Days.toISOString()),
        
        // Quiz attempts
        supabase
          .from("quiz_attempts")
          .select("id, quiz_id, score, total_questions, total_correct, completed_at, duration_seconds")
          .eq("user_id", user.id)
          .order("completed_at", { ascending: false }),
        
        // Quiz attempt answers for question type analysis
        supabase
          .from("quiz_attempt_answers")
          .select(`
            id,
            is_correct,
            question_id,
            attempt_id,
            quiz_questions!inner(question_type, prompt)
          `)
          .not("is_correct", "is", null),
        
        // Quizzes for difficulty and topics
        supabase
          .from("quizzes")
          .select("id, difficulty, topics, question_types")
          .eq("user_id", user.id),
        
        // Exam attempts
        supabase
          .from("exam_attempts")
          .select("id, exam_id, score, total_questions, total_correct, submitted_at, duration_seconds, exams!inner(title)")
          .eq("user_id", user.id)
          .eq("status", "completed")
          .order("submitted_at", { ascending: false }),
        
        // Exam attempt answers for question type analysis
        supabase
          .from("exam_attempt_answers")
          .select(`
            id,
            is_correct,
            question_id,
            attempt_id
          `)
          .not("is_correct", "is", null),
        
        // Exam questions for type mapping
        supabase
          .from("exam_questions")
          .select("id, type, exam_id"),
        
        // Chat contents (type='chat')
        supabase
          .from("content")
          .select("id, title, created_at")
          .eq("user_id", user.id)
          .eq("type", "chat")
          .order("created_at", { ascending: false }),
        
        // Chat messages for activity tracking
        supabase
          .from("chat_messages")
          .select("id, content_id, created_at, role")
          .gte("created_at", last30Days.toISOString()),
      ]);

      // Process profile data
      const profile = profileResult.data as ProfileData | null;

      // Process flashcard deck stats
      const flashcards = flashcardsResult.data || [];
      const decks = flashcardDecksResult.data || [];
      
      // Filter out orphaned decks (content_id set but content no longer exists)
      // Fetch existing content IDs to cross-reference
      const contentIds = new Set(
        decks
          .filter((d) => d.content_id)
          .map((d) => d.content_id)
      );
      
      // If there are content-linked decks, verify content still exists
      let validContentIds = new Set<string>();
      if (contentIds.size > 0) {
        const { data: existingContent } = await supabase
          .from("content")
          .select("id")
          .eq("user_id", user.id);
        validContentIds = new Set((existingContent || []).map((c) => c.id));
      }
      
      const filteredDecks = decks.filter((deck) => {
        // Keep decks with no content_id (manual/chat decks)
        if (!deck.content_id) return true;
        // Keep only if content still exists
        return validContentIds.has(deck.content_id);
      });

      const flashcardDecks: FlashcardDeckStats[] = filteredDecks.map((deck) => {
        const deckCards = flashcards.filter((c) => c.deck_id === deck.id);
        
        // Mastery levels based on SM-2 algorithm (consistent with computeStudyStats)
        const mastered = deckCards.filter((c) => (c.interval ?? 0) >= 21).length;
        const learning = deckCards.filter((c) => {
          const interval = c.interval ?? 0;
          const reps = c.repetitions ?? 0;
          // Has been studied (not never-touched) and interval < 21
          const isNeverTouched = reps === 0 && (c as any).updated_at === (c as any).created_at;
          return !isNeverTouched && interval > 0 && interval < 21;
        }).length;
        const notStudied = deckCards.filter((c) => {
          const reps = c.repetitions ?? 0;
          return reps === 0 && (c as any).updated_at === (c as any).created_at;
        }).length;

        return {
          id: deck.id,
          title: deck.title,
          total_cards: deck.total_cards,
          mastered_count: mastered,
          learning_count: learning,
          not_studied_count: notStudied,
          last_studied: deck.updated_at,
        };
      });

      // Process flashcard review ratings
      const reviews = flashcardReviewsResult.data || [];
      const ratingCounts: Record<number, number> = {};
      reviews.forEach((r) => {
        ratingCounts[r.rating] = (ratingCounts[r.rating] || 0) + 1;
      });
      const flashcardReviews: FlashcardReviewStats[] = Object.entries(ratingCounts).map(
        ([rating, count]) => ({
          rating: parseInt(rating),
          count,
        })
      );

      // Process study sessions
      const sessions = studySessionsResult.data || [];
      const sessionsByDate: Record<string, StudySessionData> = {};
      sessions.forEach((s) => {
        const date = format(new Date(s.created_at!), "yyyy-MM-dd");
        if (!sessionsByDate[date]) {
          sessionsByDate[date] = { date, studied_count: 0, due_count: 0 };
        }
        sessionsByDate[date].studied_count += s.studied_count || 0;
        sessionsByDate[date].due_count += s.due_count || 0;
      });
      const studySessions = Object.values(sessionsByDate).sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      // Weekly study sessions count
      const weeklyStudySessions = sessions.filter((s) => {
        const sessionDate = new Date(s.created_at!);
        return sessionDate >= weekStart && sessionDate <= weekEnd;
      }).length;

      // Total cards studied and due
      const totalCardsStudied = sessions.reduce((sum, s) => sum + (s.studied_count || 0), 0);
      const totalCardsDue = flashcards.filter((c) => {
        const dueAt = c.due_at ? new Date(c.due_at) : null;
        return dueAt && dueAt <= new Date();
      }).length;

      // Process quiz attempts with difficulty
      const quizzes = quizzesResult.data || [];
      const quizMap = new Map(quizzes.map((q) => [q.id, q]));
      const quizAttempts: QuizAttemptStats[] = (quizAttemptsResult.data || []).map((a) => {
        const quiz = quizMap.get(a.quiz_id);
        return {
          id: a.id,
          quiz_id: a.quiz_id,
          score: a.score,
          total_questions: a.total_questions,
          total_correct: a.total_correct,
          completed_at: a.completed_at,
          difficulty: quiz?.difficulty || "medium",
          question_types: quiz?.question_types || [],
        };
      });

      // Quiz difficulty stats
      const difficultyStats: Record<string, { total_score: number; count: number }> = {};
      quizAttempts.forEach((a) => {
        if (a.score !== null) {
          if (!difficultyStats[a.difficulty]) {
            difficultyStats[a.difficulty] = { total_score: 0, count: 0 };
          }
          difficultyStats[a.difficulty].total_score += a.score;
          difficultyStats[a.difficulty].count += 1;
        }
      });
      const quizDifficultyStats: QuizDifficultyStats[] = Object.entries(difficultyStats).map(
        ([difficulty, stats]) => ({
          difficulty,
          avg_score: stats.count > 0 ? Math.round(stats.total_score / stats.count) : 0,
          attempt_count: stats.count,
        })
      );

      // Quiz question type stats
      const quizAnswers = quizAnswersResult.data || [];
      const userQuizAttemptIds = new Set(quizAttempts.map((a) => a.id));
      const userQuizAnswers = quizAnswers.filter((a) => userQuizAttemptIds.has(a.attempt_id));
      
      const questionTypeStats: Record<string, { correct: number; total: number }> = {};
      userQuizAnswers.forEach((a) => {
        const qType = (a.quiz_questions as any)?.question_type || "unknown";
        if (!questionTypeStats[qType]) {
          questionTypeStats[qType] = { correct: 0, total: 0 };
        }
        questionTypeStats[qType].total += 1;
        if (a.is_correct) {
          questionTypeStats[qType].correct += 1;
        }
      });
      const quizQuestionTypeStats: QuizQuestionTypeStats[] = Object.entries(questionTypeStats).map(
        ([question_type, stats]) => ({
          question_type,
          correct: stats.correct,
          total: stats.total,
          accuracy: stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0,
        })
      );

      // Quiz weak topics
      const topicStats: Record<string, { correct: number; total: number }> = {};
      userQuizAnswers.forEach((a) => {
        const quiz = quizzes.find((q) => 
          quizAttempts.find((at) => at.id === a.attempt_id)?.quiz_id === q.id
        );
        const topics = quiz?.topics || [];
        topics.forEach((topic: string) => {
          if (!topicStats[topic]) {
            topicStats[topic] = { correct: 0, total: 0 };
          }
          topicStats[topic].total += 1;
          if (a.is_correct) {
            topicStats[topic].correct += 1;
          }
        });
      });
      const quizWeakTopics: WeakTopic[] = Object.entries(topicStats)
        .map(([topic, stats]) => ({
          topic,
          correct: stats.correct,
          total: stats.total,
          accuracy: stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0,
        }))
        .sort((a, b) => a.accuracy - b.accuracy)
        .slice(0, 5);

      // Overall quiz accuracy
      const totalQuizCorrect = quizAttempts.reduce((sum, a) => sum + (a.total_correct || 0), 0);
      const totalQuizQuestions = quizAttempts.reduce((sum, a) => sum + a.total_questions, 0);
      const overallQuizAccuracy = totalQuizQuestions > 0 
        ? Math.round((totalQuizCorrect / totalQuizQuestions) * 100) 
        : 0;

      // Process exam attempts
      const examAttempts: ExamAttemptStats[] = (examAttemptsResult.data || []).map((a: any) => ({
        id: a.id,
        exam_id: a.exam_id,
        score: a.score,
        total_questions: a.total_questions,
        total_correct: a.total_correct,
        submitted_at: a.submitted_at,
        title: a.exams?.title || "Untitled Exam",
      }));

      // Exam question type stats
      const examQuestions = examQuestionsResult.data || [];
      const examQuestionMap = new Map(examQuestions.map((q) => [q.id, q]));
      const examAnswers = examAnswersResult.data || [];
      const userExamAttemptIds = new Set(examAttempts.map((a) => a.id));
      const userExamAnswers = examAnswers.filter((a) => userExamAttemptIds.has(a.attempt_id));

      const examTypeStats: Record<string, { correct: number; total: number }> = {};
      userExamAnswers.forEach((a) => {
        const question = examQuestionMap.get(a.question_id);
        const qType = question?.type || "unknown";
        if (!examTypeStats[qType]) {
          examTypeStats[qType] = { correct: 0, total: 0 };
        }
        examTypeStats[qType].total += 1;
        if (a.is_correct) {
          examTypeStats[qType].correct += 1;
        }
      });
      const examQuestionTypeStats: ExamQuestionTypeStats[] = Object.entries(examTypeStats).map(
        ([question_type, stats]) => ({
          question_type,
          correct: stats.correct,
          total: stats.total,
          accuracy: stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0,
        })
      );

      // Overall exam accuracy
      const totalExamCorrect = examAttempts.reduce((sum, a) => sum + (a.total_correct || 0), 0);
      const totalExamQuestions = examAttempts.reduce((sum, a) => sum + a.total_questions, 0);
      const overallExamAccuracy = totalExamQuestions > 0 
        ? Math.round((totalExamCorrect / totalExamQuestions) * 100) 
        : 0;

      // Process chat stats
      const chatContents = chatContentsResult.data || [];
      const chatMessages = chatMessagesResult.data || [];
      const userChatIds = new Set(chatContents.map((c) => c.id));
      const userChatMessages = chatMessages.filter((m) => userChatIds.has(m.content_id));
      
      const totalChats = chatContents.length;
      const totalMessages = userChatMessages.length;
      const avgMessagesPerChat = totalChats > 0 ? Math.round(totalMessages / totalChats) : 0;
      
      // Activity by day
      const messagesByDay: Record<string, number> = {};
      userChatMessages.forEach((m) => {
        const date = format(new Date(m.created_at), "MMM d");
        messagesByDay[date] = (messagesByDay[date] || 0) + 1;
      });
      const activityByDay = Object.entries(messagesByDay).map(([date, messages]) => ({
        date,
        messages,
      }));
      
      // Top topics from chat titles
      const topTopics = chatContents
        .map((c) => ({
          title: c.title || "Untitled Chat",
          messageCount: userChatMessages.filter((m) => m.content_id === c.id).length,
        }))
        .sort((a, b) => b.messageCount - a.messageCount)
        .slice(0, 6);

      const chatStats: ChatStats = {
        totalChats,
        totalMessages,
        avgMessagesPerChat,
        activityByDay,
        topTopics,
      };

      // Calculate study time from existing data
      // Flashcards: use study sessions if available, otherwise estimate from reviews (~30s per review)
      let flashcardsMinutes = Math.round((totalCardsStudied * 30) / 60);
      if (flashcardsMinutes === 0 && reviews.length > 0) {
        // Fallback: estimate from review count
        flashcardsMinutes = Math.round((reviews.length * 30) / 60);
      }
      
      // Quizzes: use duration_seconds from quiz_attempts
      const quizzesMinutes = Math.round(
        (quizAttemptsResult.data || []).reduce((sum, a) => sum + (a.duration_seconds || 0), 0) / 60
      );
      
      // Exams: use duration_seconds from exam_attempts
      const examsMinutes = Math.round(
        (examAttemptsResult.data || []).reduce((sum, a) => sum + (a.duration_seconds || 0), 0) / 60
      );
      
      const totalMinutes = flashcardsMinutes + quizzesMinutes + examsMinutes;
      
      // Weekly study time (from sessions in current week)
      const weeklyFlashcardMinutes = Math.round(
        sessions.filter((s) => {
          const sessionDate = new Date(s.created_at!);
          return sessionDate >= weekStart && sessionDate <= weekEnd;
        }).reduce((sum, s) => sum + ((s.studied_count || 0) * 30) / 60, 0)
      );
      
      const weeklyQuizMinutes = Math.round(
        (quizAttemptsResult.data || [])
          .filter((a) => a.completed_at && new Date(a.completed_at) >= weekStart && new Date(a.completed_at) <= weekEnd)
          .reduce((sum, a) => sum + (a.duration_seconds || 0), 0) / 60
      );
      
      const weeklyExamMinutes = Math.round(
        (examAttemptsResult.data || [])
          .filter((a) => a.submitted_at && new Date(a.submitted_at) >= weekStart && new Date(a.submitted_at) <= weekEnd)
          .reduce((sum, a) => sum + (a.duration_seconds || 0), 0) / 60
      );
      
      const weeklyMinutes = weeklyFlashcardMinutes + weeklyQuizMinutes + weeklyExamMinutes;
      const dailyAverage = Math.round(weeklyMinutes / 7);
      
      // Breakdown by activity
      const breakdown: { activity: string; minutes: number; percentage: number }[] = [];
      if (totalMinutes > 0) {
        if (flashcardsMinutes > 0) {
          breakdown.push({
            activity: "Flashcards",
            minutes: flashcardsMinutes,
            percentage: Math.round((flashcardsMinutes / totalMinutes) * 100),
          });
        }
        if (quizzesMinutes > 0) {
          breakdown.push({
            activity: "Quizzes",
            minutes: quizzesMinutes,
            percentage: Math.round((quizzesMinutes / totalMinutes) * 100),
          });
        }
        if (examsMinutes > 0) {
          breakdown.push({
            activity: "Exams",
            minutes: examsMinutes,
            percentage: Math.round((examsMinutes / totalMinutes) * 100),
          });
        }
      }
      
      const studyTime: StudyTimeStats = {
        totalMinutes,
        flashcardsMinutes,
        quizzesMinutes,
        examsMinutes,
        weeklyMinutes,
        dailyAverage,
        breakdown,
      };

      return {
        profile,
        flashcardDecks,
        flashcardReviews,
        studySessions,
        weeklyStudySessions,
        totalCardsStudied,
        totalCardsDue,
        quizAttempts,
        quizDifficultyStats,
        quizQuestionTypeStats,
        quizWeakTopics,
        examAttempts,
        examQuestionTypeStats,
        overallQuizAccuracy,
        overallExamAccuracy,
        chatStats,
        studyTime,
      };
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
